import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BetLimitCandidate, BetLimitMemberRow, BetLimitProviderOption } from '@core/models/bet-limit-group.model';
import { Pagination } from '@core/models/pagination.model';
import { Subscription } from 'rxjs';
import { BetLimitGroupDataService } from '../../../services/bet-limit-group-data.service';
import { AddPlayersDialogComponent } from '../../../dialogs/add-players/add-players.component';

interface PlayerRow {
  id: number;
  username: string;
  currency_code: string;
  member_group: string | null;
  added_by: string | null;
  staged: 'add' | 'remove' | null;
}

/**
 * AIO-81599 — step 3: the group's players.
 *
 * Adds and removals are staged in the parent's maps and saved with the rest of the group.
 * Assigned players are paged from the server — a group can hold thousands. Staged adds are paged
 * on the client and come first: pages 1..addPages are staged adds, the rest are server pages.
 */
@Component({
  selector: 'kt-bet-limit-players-step',
  templateUrl: './players-step.component.html',
  styleUrls: ['./players-step.component.scss'],
})
export class PlayersStepComponent implements OnChanges, OnDestroy {
  @Input() groupId: number = null;
  @Input() groupName = '';
  @Input() provider: BetLimitProviderOption;
  @Input() membersCount = 0;
  @Input() stagedAdd: Map<number, BetLimitCandidate>;
  @Input() stagedRemove: Set<number>;
  @Input() revision = 0;
  @Input() readonly = false;
  @Input() loadCount = 0;
  @Output() changed = new EventEmitter<void>();

  serverRows: BetLimitMemberRow[] = [];

  /** Shown as "Added By" on staged adds: the save records this operator. */
  private readonly operatorName: string = JSON.parse(localStorage.getItem('user_data'))?.username ?? '-';
  pagination: Pagination;
  page = 1;
  pageSize = 25;
  maxSize = 5;
  loading = false;
  username = '';
  selected = new Set<number>();

  private appliedUsername = '';
  private subscriptions = new Subscription();

  constructor(
    private betLimitGroupDataService: BetLimitGroupDataService,
    private dialog: MatDialog,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    // The parent reloads the group after a save (loadCount): fetch the saved list again, also
    // when the count did not change (one add and one removal).
    if (changes.groupId || changes.membersCount || changes.loadCount) {
      this.selected.clear();
      this.load(1);
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  get addedRows(): PlayerRow[] {
    const filter = this.appliedUsername.toLowerCase();
    return Array.from(this.stagedAdd.values())
      .filter(c => !filter || c.username.toLowerCase().includes(filter))
      .map(c => ({ id: c.id, username: c.username, currency_code: c.currency_code, member_group: c.member_group, added_by: this.operatorName, staged: 'add' as const }));
  }

  get addPages(): number {
    return Math.ceil(this.addedRows.length / this.pageSize);
  }

  get serverPages(): number {
    return Math.ceil((this.pagination?.total ?? 0) / this.pageSize);
  }

  get totalPages(): number {
    return this.addPages + this.serverPages;
  }

  get onAddPage(): boolean {
    return this.page <= this.addPages;
  }

  get rows(): PlayerRow[] {
    if (this.onAddPage) {
      return this.addedRows.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
    }
    return this.serverRows.map(r => ({
      id: r.member_account_id, username: r.username, currency_code: r.currency_code, member_group: r.member_group,
      added_by: r.added_by, staged: this.stagedRemove.has(r.member_account_id) ? 'remove' as const : null,
    }));
  }

  get pageDescription(): string {
    if (this.onAddPage) {
      const total = this.addedRows.length;
      const from = (this.page - 1) * this.pageSize + 1;
      return `Showing ${from} to ${Math.min(this.page * this.pageSize, total)} of ${total} players to add`;
    }
    return `Showing ${this.pagination?.from || 0} to ${this.pagination?.to || 0} of ${this.pagination?.total ?? 0} saved players`;
  }

  get allSelected(): boolean {
    const rows = this.rows;
    return rows.length > 0 && rows.every(r => this.selected.has(r.id));
  }

  load(page = this.page) {
    this.page = page;
    if (!this.groupId) {
      this.serverRows = [];
      this.pagination = undefined;
      return;
    }
    // A staged-add page needs no fetch once the saved total is known.
    if (this.onAddPage && this.pagination) {
      return;
    }

    this.loading = true;
    const serverPage = Math.max(1, page - this.addPages);
    const filter = this.appliedUsername ? `&username=${encodeURIComponent(this.appliedUsername)}` : '';
    this.subscriptions.add(
      this.betLimitGroupDataService.getMembers(this.groupId, `?page=${serverPage}&perPage=${this.pageSize}${filter}`).subscribe(
        data => {
          this.serverRows = data.rows;
          this.pagination = data.paginations;
          this.loading = false;
        },
        () => this.loading = false
      )
    );
  }

  onSearch() {
    this.appliedUsername = (this.username || '').trim();
    this.pagination = undefined;   // the saved total depends on the filter: force a fetch
    this.load(1);
  }

  onClear() {
    this.username = '';
    this.onSearch();
  }

  /**
   * `rows` builds new objects on every change detection. Without this, Angular rebuilds every
   * row each time, so a click that spans a rebuild (checkbox, remove, undo) is lost.
   */
  trackByRow(_: number, row: PlayerRow) {
    return row.id;
  }

  onToggle(row: PlayerRow, checked: boolean) {
    checked ? this.selected.add(row.id) : this.selected.delete(row.id);
  }

  onToggleAll(checked: boolean) {
    this.rows.forEach(r => checked ? this.selected.add(r.id) : this.selected.delete(r.id));
  }

  /** A staged add is dropped; a saved player is marked for removal (click again to undo). */
  onRemove(row: PlayerRow) {
    if (this.readonly) {
      return;
    }
    if (row.staged === 'add') {
      this.stagedAdd.delete(row.id);
      this.reloadAfterStagedAddChange();
    } else if (this.stagedRemove.has(row.id)) {
      this.stagedRemove.delete(row.id);
    } else {
      this.stagedRemove.add(row.id);
    }
    this.selected.delete(row.id);
    this.changed.emit();
  }

  /** Works on ids, not on this page's rows: the selection can span pages. */
  onRemoveSelected() {
    if (this.readonly) {
      return;
    }
    this.selected.forEach(id => this.stagedAdd.has(id) ? this.stagedAdd.delete(id) : this.stagedRemove.add(id));
    this.selected.clear();
    this.reloadAfterStagedAddChange();
    this.changed.emit();
  }

  /** The number of staged-add pages changed, so a page number can now point past the end. */
  private reloadAfterStagedAddChange(page = this.page) {
    this.load(Math.min(page, Math.max(1, this.totalPages)));
  }

  onAddPlayers() {
    if (this.readonly) {
      return;
    }
    const ref = this.dialog.open(AddPlayersDialogComponent, {
      width: '1000px',
      autoFocus: false,
      data: { provider: this.provider, groupId: this.groupId, groupName: this.groupName },
    });

    this.subscriptions.add(ref.afterClosed().subscribe((picked: BetLimitCandidate[]) => {
      if (!picked?.length) {
        return;
      }
      picked.forEach(c => {
        if (this.stagedRemove.has(c.id)) {
          this.stagedRemove.delete(c.id);          // was being removed: just keep it
        } else if (!this.groupId || +c.current_group_id !== +this.groupId) {
          this.stagedAdd.set(c.id, c);             // new, or moving from another group
        }
      });
      this.reloadAfterStagedAddChange(1);         // show the new adds
      this.changed.emit();
    }));
  }
}
